import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Mail, Phone, Check, ShieldCheck, Home, Anchor } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { FOOD_IMAGES } from "@/data/content";
import Reveal from "@/components/Reveal";
import { trackEvent, trackPageView } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/0ymt0tjh_IMG_1787.jpeg";

// Saudi-first landing copy — Arabic primary, English secondary
const COPY = {
  ar: {
    eyebrowTop: "خاص للعائلات في المملكة العربية السعودية",
    heroTitle: "العافية الفاخرة، تُخدَم في منزلك. بسرّية تامة.",
    heroLead:
      "تصمّم كارول ستوفيلا برامج إطالة العمر والتغذية الدقيقة لعائلات المملكة، تُنفَّذ داخل قصرك الخاص، أو ضيافتك، أو يختك — مع طاهية محترفة وقوائم متوافقة مع المطبخ المحلي والشريعة.",
    ctaWa: "تواصل عبر واتساب الخاص",
    ctaForm: "اطلب استشارة سرّية",
    pillarsEyebrow: "لماذا العائلات في الرياض، جدة وأبوظبي تختار كارول",
    pillars: [
      {
        icon: "shield",
        t: "السرّية أولاً",
        d: "اتفاقيات سرّية، لا حضور علني، لا إعلانات. فريقنا يعمل بصمت داخل بيتك.",
      },
      {
        icon: "home",
        t: "في إقامتك",
        d: "نحضر إلى قصرك أو شقتك الفندقية أو يختك. نُدرّب الطاهي، ونُعيد هيكلة المطبخ.",
      },
      {
        icon: "halal",
        t: "موافق للشريعة ومحلي",
        d: "وصفات صحّية مبنيّة على المطبخ السعودي والخليجي: الكبسة الخفيفة، السمك المشوي، الحمص، التمور، الأعشاب.",
      },
      {
        icon: "anchor",
        t: "للنساء أيضاً",
        d: "خيار طاقم نسائي بالكامل عند الطلب. خصوصية كاملة لقسم الحريم.",
      },
    ],
    forEyebrow: "خدمتنا الرئيسية في المملكة",
    forTitle: "سبا في المنزل — بروتوكول 4 أسابيع.",
    forSteps: [
      "تقييم سريري ومؤشرات حيوية",
      "خطة دقيقة شخصية للأسرة",
      "تنفيذ في المطبخ مع تدريب الطاهي",
      "متابعة مستمرة عبر فريقنا",
    ],
    contactEyebrow: "ابدأ الآن — رد خلال 24 ساعة",
    contactTitle: "محادثة خاصة. لا التزام.",
    qualifierLabel: "أنت تتواصل بصفة:",
    qualifierOptions: [
      { value: "principal", label: "عميل خاص / صاحب القرار" },
      { value: "family-office", label: "مكتب عائلي" },
      { value: "hospitality", label: "مجموعة ضيافة / فندق" },
      { value: "other", label: "أخرى (يخت، إقامة)" },
    ],
    formName: "الاسم الكامل",
    formCity: "المدينة (الرياض، جدة، أبوظبي…)",
    formPhone: "رقم الهاتف / واتساب",
    formMessage: "ما الذي تتمنى تحقيقه؟",
    formSubmit: "إرسال الطلب",
    formSending: "جارٍ الإرسال…",
    successTitle: "شكراً لك.",
    successBody: "وصل طلبك إلى مكتب كارول. سنتواصل خلال 24 ساعة بأقصى سرية.",
    secondaryEn: "Switch to English",
  },
  en: {
    eyebrowTop: "Private practice for families in Saudi Arabia",
    heroTitle: "Luxury wellbeing, delivered to your home. In total discretion.",
    heroLead:
      "Carol Stoffella designs longevity and precision-nutrition programs for families in the Kingdom — executed inside your residence, hospitality property or yacht, with a senior female-led team and menus adapted to local cuisine and Sharia.",
    ctaWa: "Private WhatsApp",
    ctaForm: "Request a confidential consultation",
    pillarsEyebrow: "Why families in Riyadh, Jeddah and Abu Dhabi choose Carol",
    pillars: [
      {
        icon: "shield",
        t: "Discretion first",
        d: "NDAs by default. No public mention, no advertising. Our team works silently inside your home.",
      },
      {
        icon: "home",
        t: "In your residence",
        d: "We come to your palace, suite or yacht. We train your chef and re-architect the kitchen.",
      },
      {
        icon: "halal",
        t: "Halal & locally adapted",
        d: "Health-focused recipes built around Saudi and Gulf cuisine: lightened kabsa, grilled fish, hummus, dates, fresh herbs.",
      },
      {
        icon: "anchor",
        t: "Female-led available",
        d: "Fully female team available on request. Total privacy for the family quarters.",
      },
    ],
    forEyebrow: "Our flagship solution in the Kingdom",
    forTitle: "Spa at Home — a four-week protocol.",
    forSteps: [
      "Clinical assessment and biomarker testing",
      "Personalised precision plan for the family",
      "In-kitchen execution with chef training",
      "Continuous follow-up by our team",
    ],
    contactEyebrow: "Begin now — response within 24 hours",
    contactTitle: "A private conversation. No commitment.",
    qualifierLabel: "You are reaching out as:",
    qualifierOptions: [
      { value: "principal", label: "Private client / Principal" },
      { value: "family-office", label: "Family office" },
      { value: "hospitality", label: "Hospitality group / Hotel" },
      { value: "other", label: "Other (yacht, residence)" },
    ],
    formName: "Full name",
    formCity: "City (Riyadh, Jeddah, Abu Dhabi…)",
    formPhone: "Phone / WhatsApp",
    formMessage: "What would you like to achieve?",
    formSubmit: "Send request",
    formSending: "Sending…",
    successTitle: "Thank you.",
    successBody: "Your request has reached Carol's office. We will reply within 24 hours in complete confidentiality.",
    secondaryEn: "التبديل إلى العربية",
  },
};

const ICONS = {
  shield: ShieldCheck,
  home: Home,
  halal: Check,
  anchor: Anchor,
};

export default function SaudiLanding() {
  const { setLang } = useLang();
  const navigate = useNavigate();
  const [variant, setVariant] = useState("ar");
  const c = COPY[variant];
  const isAr = variant === "ar";
  const [form, setForm] = useState({ name: "", city: "", phone: "", message: "", leadType: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Force RTL when Arabic on this landing only — restore on unmount
  useEffect(() => {
    trackPageView(`/sa?lang=${variant}`);
    const html = document.documentElement;
    const prevLang = html.getAttribute("lang") || "en";
    const prevDir = html.getAttribute("dir") || "ltr";
    html.setAttribute("lang", variant);
    html.setAttribute("dir", isAr ? "rtl" : "ltr");
    return () => {
      html.setAttribute("lang", prevLang);
      html.setAttribute("dir", prevDir);
      const stored = window.localStorage.getItem("cs_lang");
      if (stored) setLang(stored);
    };
  }, [variant, isAr, setLang]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message || !form.leadType) {
      toast.error(isAr ? "يرجى إكمال الحقول" : "Please complete all required fields");
      return;
    }
    setSubmitting(true);
    trackEvent("form_submit", { lead_type: form.leadType, locale: variant, page: "/sa" });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: `${form.phone.replace(/[^0-9a-zA-Z]/g, "") || "lead"}@no-reply.carolstoffella.com`,
        phone: form.phone,
        interest: "uhnw-advisory",
        lead_type: form.leadType,
        message: `[GCC LANDING / ${variant.toUpperCase()}] City: ${form.city || "—"}\n\n${form.message}`,
        locale: variant,
      });
      setSuccess(true);
      setForm({ name: "", city: "", phone: "", message: "", leadType: "" });
      toast.success(c.successTitle);
      trackEvent("form_success", { lead_type: form.leadType, locale: variant, page: "/sa" });
      // Redirect to thank-you page for cleaner Meta/GA4 conversion attribution
      navigate(`/sa/thank-you?lang=${variant}&lead_type=${encodeURIComponent(form.leadType)}`);
    } catch {
      toast.error(isAr ? "حدث خطأ" : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-saudi-landing" className="min-h-screen bg-[#070E13] text-[#F4EEE8]">
      {/* Slim top bar */}
      <header className="absolute top-0 inset-x-0 z-30 px-6 sm:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 text-[#F4EEE8]/70 hover:text-[#F4EEE8] text-xs tracking-widest uppercase">
          <ArrowLeft size={14} /> {isAr ? "الموقع الكامل" : "Full site"}
        </Link>
        <button
          data-testid="lang-switch"
          onClick={() => setVariant(isAr ? "en" : "ar")}
          className="text-xs tracking-widest uppercase text-[#C7A26C] hover:text-[#E8DFC8]"
        >
          {c.secondaryEn}
        </button>
      </header>

      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <img src={FOOD_IMAGES.luxuryKitchen} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E13] via-[#070E13]/65 to-[#070E13]/30" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 pb-20 pt-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end w-full">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline text-[#C7A26C]">{c.eyebrowTop}</p>
            </Reveal>
            <Reveal delay={120}>
              <h1
                className={`h-editorial mt-6 ${isAr ? "text-4xl sm:text-5xl lg:text-6xl" : "text-5xl sm:text-6xl lg:text-7xl"}`}
              >
                {c.heroTitle}
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 max-w-2xl text-lg text-[#F4EEE8]/85 leading-relaxed">{c.heroLead}</p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="https://wa.me/971558441468"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sa-cta-whatsapp"
                  onClick={() => trackEvent("whatsapp_click", { source: "hero", locale: variant })}
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#128C7E] transition-colors"
                >
                  <MessageCircle size={16} /> {c.ctaWa}
                </a>
                <a
                  href="#concierge-form"
                  data-testid="sa-cta-form"
                  className="inline-flex items-center gap-2 border border-[#C7A26C] text-[#C7A26C] px-7 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#C7A26C] hover:text-[#F4EEE8] transition-colors"
                >
                  {c.ctaForm}
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={300} className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <img src={PORTRAIT} alt="Carol Stoffella" className="w-full aspect-[4/5] object-cover" />
              <span className="absolute -bottom-3 left-0 w-32 h-px bg-[#2FB7B1]" />
              <span className="absolute -top-3 right-0 w-20 h-px bg-[#C77D66]" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#070E13] to-transparent p-6">
                <p className="font-serif text-2xl">Carol Stoffella</p>
                <p className="text-xs text-[#C7A26C] tracking-widest uppercase mt-1">
                  {isAr ? "طاهية · استراتيجية ضيافة وإطالة العمر" : "Chef · Hospitality & Longevity Strategist"}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#0C1821] text-[#F4EEE8] py-24 sm:py-32 border-y border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline text-[#C7A26C]">{c.pillarsEyebrow}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {c.pillars.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal
                  key={i}
                  delay={i * 80}
                  className="relative bg-[#070E13] text-[#F4EEE8] p-8 border-s-2 border-[#2FB7B1]"
                >
                  <span className="absolute top-0 right-0 w-12 h-px bg-[#C77D66]" />
                  <Icon className="text-[#C7A26C]" size={28} strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl mt-6">{p.t}</h3>
                  <p className="mt-3 text-sm text-[#F4EEE8]/75 leading-relaxed">{p.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE */}
      <section className="bg-[#070E13] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <img src={FOOD_IMAGES.blackCodGreens} alt="" className="w-full aspect-[5/4] object-cover" />
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6">
            <p className="overline text-[#C7A26C]">{c.forEyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{c.forTitle}</h2>
            <ul className="mt-10 space-y-5">
              {c.forSteps.map((s, i) => (
                <li key={i} className="flex gap-4 items-start text-[#F4EEE8]/85 border-b border-white/10 pb-5">
                  <span className="font-serif text-2xl text-[#C7A26C] leading-none">0{i + 1}</span>
                  <span className="text-lg">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="concierge-form" className="bg-[#0C1821] text-[#F4EEE8] py-24 sm:py-32 border-y border-[#C7A26C]/15">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline text-[#C7A26C]">{c.contactEyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{c.contactTitle}</h2>
          </Reveal>
          {success ? (
            <div data-testid="sa-success" className="mt-12 bg-[#070E13] text-[#F4EEE8] p-10 sm:p-14 border-s-2 border-[#2FB7B1] relative">
              <span className="absolute top-0 right-0 w-20 h-px bg-[#C77D66]" />
              <p className="overline text-[#C7A26C]">✦ {c.successTitle}</p>
              <p className="font-serif text-3xl mt-4">{c.successBody}</p>
              <a
                href="https://wa.me/971558441468"
                target="_blank"
                rel="noreferrer"
                data-testid="sa-success-whatsapp"
                className="inline-flex items-center gap-2 mt-10 bg-[#25D366] text-white px-7 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#128C7E] transition-colors"
              >
                <MessageCircle size={16} /> {c.ctaWa}
              </a>
            </div>
          ) : (
            <form data-testid="sa-form" onSubmit={submit} className="mt-12 space-y-7">
              {/* PRE-QUALIFIER — single question, big impact */}
              <div>
                <p className="overline text-[#D6D1C9]/70 mb-4">
                  {c.qualifierLabel}<span className="text-[#DE3C30] ms-1">*</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {c.qualifierOptions.map((opt) => {
                    const active = form.leadType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`sa-leadtype-${opt.value}`}
                        onClick={() => {
                          setForm({ ...form, leadType: opt.value });
                          trackEvent("lead_qualifier_selected", { lead_type: opt.value, locale: variant });
                        }}
                        className={`text-start px-5 py-4 border transition-all duration-300 ${
                          active
                            ? "bg-[#070E13] text-[#F4EEE8] border-[#2FB7B1] border-s-2"
                            : "bg-transparent text-[#070E13] border-[#070E13]/25 hover:border-[#C7A26C]"
                        }`}
                      >
                        <span className="font-serif text-lg leading-tight block">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label={c.formName} name="name" value={form.name} onChange={handle} testid="sa-name" required />
              <Field label={c.formCity} name="city" value={form.city} onChange={handle} testid="sa-city" />
              <Field label={c.formPhone} name="phone" value={form.phone} onChange={handle} testid="sa-phone" required />
              <div>
                <label className="overline text-[#D6D1C9]/70 block mb-3" htmlFor="message">
                  {c.formMessage}
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="sa-message"
                  value={form.message}
                  onChange={handle}
                  rows={4}
                  required
                  className="w-full bg-transparent border-b border-[#C7A26C]/30 focus:border-[#C7A26C] outline-none py-3 text-[#070E13] resize-none transition-colors"
                />
              </div>
              <div className="flex flex-wrap gap-4 items-center pt-4">
                <button
                  type="submit"
                  data-testid="sa-submit"
                  disabled={submitting}
                  className="bg-[#070E13] text-[#F4EEE8] px-8 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#14222C] transition-colors disabled:opacity-50"
                >
                  {submitting ? c.formSending : c.formSubmit}
                </button>
                <a
                  data-testid="sa-form-whatsapp"
                  href="https://wa.me/971558441468"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase border border-[#25D366] text-[#070E13] hover:bg-[#25D366] hover:text-white transition-colors"
                >
                  <MessageCircle size={16} /> {c.ctaWa}
                </a>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* DIRECT FOOTER */}
      <footer className="bg-[#070E13] py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 flex flex-col md:flex-row gap-6 justify-between items-start text-sm text-[#F4EEE8]/70">
          <div>
            <img src="/logo-white.png" alt="Carol Stoffella" className="h-5 w-auto mb-4" />
            <p className="font-serif italic">
              {isAr ? "السرية لغة الفخامة." : "Discretion is the language of luxury."}
            </p>
          </div>
          <ul className="space-y-2">
            <li className="flex items-center gap-2"><Mail size={14} /> <a href="mailto:carol@carolstoffella.com" className="hover:text-[#C7A26C]">carol@carolstoffella.com</a></li>
            <li className="flex items-center gap-2"><Phone size={14} /> <a href="https://wa.me/971558441468" target="_blank" rel="noreferrer" className="hover:text-[#C7A26C]">+971 55 844 1468 — WhatsApp</a></li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline text-[#D6D1C9]/70 block mb-3" htmlFor={name}>
        {label}{required && <span className="text-[#DE3C30] ms-1">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#C7A26C]/30 focus:border-[#C7A26C] outline-none py-3 text-[#F4EEE8] transition-colors"
      />
    </div>
  );
}
